// BudgetCircularProgress.tsx - Updated version
import React from 'react';
import { useTranslation } from 'react-i18next';
import { PolarAngleAxis, RadialBar, RadialBarChart, ResponsiveContainer } from 'recharts';
import styled, { useTheme } from 'styled-components';

interface BudgetCircularProgressProps {
  currencySymbol: string;
  percentage: number;
  spent: number;
  total: number;
  isOverBudget?: boolean;
}

export const BudgetCircularProgress: React.FC<BudgetCircularProgressProps> = ({
  percentage,
  spent,
  total,
  currencySymbol,
  isOverBudget = false,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();

  // When over budget, we don't show the progress bar
  const data = [
    {
      name: 'Spent',
      value: isOverBudget ? 0 : Math.min(percentage, 100),
      fill: theme.palette.brand.editorTabPrimary,
    },
  ];

  const formattedSpent = `${currencySymbol}${spent.toLocaleString()}`;
  const formattedTotal = `${currencySymbol}${total.toLocaleString()}`;

  // Use absolute value for percentage display
  const percentageDisplay = isOverBudget
    ? `-${Math.abs(percentage).toFixed(0)}%`
    : `${percentage.toFixed(0)}%`;

  return (
    <ChartContainer title={`${formattedSpent} of ${formattedTotal}`}>
      <ResponsiveContainer>
        <RadialBarChart
          cx="50%"
          cy="50%"
          innerRadius="95%"
          outerRadius="95%"
          barSize={14}
          data={data}
          startAngle={90}
          endAngle={450}
        >
          <PolarAngleAxis type="number" domain={[0, 100]} angleAxisId={0} tick={false} />
          <RadialBar
            dataKey="value"
            background={{ fill: '#fff' }}
            cornerRadius={0}
            style={{
              filter:
                'drop-shadow(0px 2px 4px rgba(0, 0, 0, 0.1)) drop-shadow(inset 0px 1px 2px rgba(0, 0, 0, 0.1))',
            }}
          />
        </RadialBarChart>
      </ResponsiveContainer>
      <OuterCircle $isOverBudget={isOverBudget} />
      <InnerCircle $isOverBudget={isOverBudget} />
      <ChartContent>
        <PercentageText $isOverBudget={isOverBudget}>{percentageDisplay}</PercentageText>
        <SpentText $isOverBudget={false}>
          {t('editor.sidebar.initiative.budget.remaining')}
        </SpentText>
      </ChartContent>
    </ChartContainer>
  );
};

const ChartContainer = styled.div`
  width: 125px;
  height: 125px;
  position: relative;
`;

const OuterCircle = styled.div<{ $isOverBudget: boolean }>`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 125px;
  height: 125px;
  border-radius: 50%;
  border: 2px solid ${({ $isOverBudget }) => ($isOverBudget ? '#c92c2d' : 'transparent')};
  pointer-events: none;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.15), 0px 4px 8px rgba(0, 0, 0, 0.1);
`;

const InnerCircle = styled.div<{ $isOverBudget: boolean }>`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 97px;
  height: 97px;
  border-radius: 50%;
  border: 2px solid ${({ $isOverBudget }) => ($isOverBudget ? '#c92c2d' : 'transparent')};
  pointer-events: none;
  box-shadow: inset 0px 2px 4px rgba(0, 0, 0, 0.15), inset 0px 1px 2px rgba(0, 0, 0, 0.1);
`;

const ChartContent = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
`;

const PercentageText = styled.p<{ $isOverBudget: boolean }>`
  font-size: 2.5rem;
  font-weight: 700;
  color: ${({ $isOverBudget, theme }) =>
    $isOverBudget ? '#c92c2d' : theme.palette.brand.editorTabPrimary};
  margin: 0;
  line-height: 1;
`;

const SpentText = styled.p<{ $isOverBudget: boolean }>`
  font-size: 1.3rem;
  font-weight: 700;
  color: ${({ $isOverBudget, theme }) =>
    $isOverBudget ? '#c92c2d' : theme.palette.brand.editorTabPrimary};
  margin: 0.5rem 0 0 0;
`;
